import { useState, useContext } from 'react';
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttNoResults } from '../../../sporttia/all';
import type { DeviceSpace } from '../../../types/models/DeviceSpace';
import useScsService from '../../../services/ScsService';
import DeviceSpaceDialog from '../../../components/dialogs/DeviceSpaceDialog';

/**
 * Color del espacio según lo lleno que esté: verde con holgura, ámbar cuando se
 * acerca al límite y rojo al pasar del 80%. Devuelve tokens del tema, nunca
 * colores en crudo.
 */
function getDeviceSpaceColor(
    currentCapacity: number,
    maximumCapacity: number,
): string {
    const percentageFilled = currentCapacity / maximumCapacity;

    if (percentageFilled < 0.5) return 'success.main';
    if (percentageFilled < 0.8) return 'warning.main';
    return 'error.main';
}

/**
 * Monitor de aforo: una baldosa por espacio con su ocupación actual, que se
 * refresca cada cinco minutos. Al pulsar una se abre su detalle.
 *
 * Sólo pinta el contenido: la cabecera y el título los pone la página
 * (`CapacityMonitorPage`).
 */
export default function CapacityMonitorContent(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const scsService = useScsService();
    const [deviceSpace, setDeviceSpace] = useState<DeviceSpace | null>(null);
    const devicesSpacesQuery = scsService.useGetScsDevicesCapacities(
        cxt?.sc?.id ?? 0,
        { trash: false },
        {
            refetchInterval: 300000, // milliseconds
        },
    );

    if (devicesSpacesQuery.isLoading) {
        return (
            <Box display="flex" justifyContent="center" paddingY={15}>
                <CircularProgress />
            </Box>
        );
    }

    const deviceSpaces = devicesSpacesQuery?.data?.rows ?? [];

    // Un centro sin espacios de dispositivos no tiene aforo que vigilar: se
    // avisa en vez de dejar la pantalla en blanco.
    if (deviceSpaces.length === 0) {
        return <SttNoResults text={t('deviceSpace.noDeviceSpaces')} />;
    }

    return (
        <>
            <Grid container spacing={3} direction="row">
                {deviceSpaces.map((item: DeviceSpace) => (
                    <Grid key={item.id}>
                        <Paper
                            component="button"
                            type="button"
                            onClick={() => setDeviceSpace(item)}
                            sx={{
                                display: 'flex',
                                height: 40,
                                width: 'auto',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 1.25,
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: getDeviceSpaceColor(
                                    item?.currentCapacity,
                                    item?.maximumCapacity,
                                ),
                            }}
                        >
                            <Typography
                                noWrap
                                sx={{
                                    color: 'common.white',
                                    fontWeight: 'bold',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {`${item?.name}: ${item?.currentCapacity} / ${item?.maximumCapacity}`}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {deviceSpace && (
                <DeviceSpaceDialog
                    item={deviceSpace}
                    onSave={() => {
                        setDeviceSpace(null);
                        devicesSpacesQuery.refetch();
                    }}
                    onClose={() => {
                        setDeviceSpace(null);
                    }}
                />
            )}
        </>
    );
}
